import Node from '../node.js';
import { PARAM_TYPE } from '../connector.js';
import CompoundParameter from '../compoundParameter.js';
import materialTweakingFragmentShader from '../../../shaders.js';

export const SHADER_INPUT_NAMES = {
	'brightness':		'B',
	'contrast':			'C',
	'hue':				'H',
	'saturation':		'S',
	'cyan_red':			'Bcr',
	'magenta_green':	'Bmg',
	'yellow_blue':		'Byb',
};

export default class NodeMaterialTweaks extends Node
{
	constructor(pParent)
	{
		super(pParent);

		this.addInput(PARAM_TYPE.GEOMETRY, 'geo', null);
		this.addInput(PARAM_TYPE.MATERIAL_TWEAKS, 'tweaks', CompoundParameter.create(PARAM_TYPE.MATERIAL_TWEAKS));

		this.addOutput(PARAM_TYPE.GEOMETRY, 'geo');

		if(!BABYLON.Effect.ShadersStore.TweakPixelShader)
			BABYLON.Effect.ShadersStore.TweakPixelShader = materialTweakingFragmentShader;

		this.cachedTextures = [];

		this.recheckTimeout = null;
	}

	dispose()
	{
		super.dispose();
		for(let pInfo of this.cachedTextures)
			pInfo.texture.dispose();
		this.cachedTextures = [];
	}

	getType()
	{
		return 'materialTweaks';
	}

	getCachedTexture(url)
	{
		for(let pCachedInfo of this.cachedTextures)
		{
			if(pCachedInfo.url === url && !pCachedInfo.isUsed)
			{
				pCachedInfo.isUsed = true;
				return pCachedInfo.texture;
			}
		}

		return null;
	}

	getHaveNonDefaultValues(tweaksResult)
	{
		let tweakParams = tweaksResult.getParameterInfos();
		let bHaveNonDefaultValues = false;
		for(let parmInfo of tweakParams)
		{
			if(Math.abs(parmInfo.value) > 0.001)
			{
				bHaveNonDefaultValues = true;
				break;
			}
		}

		return bHaveNonDefaultValues;
	}

	saveToMaterialTweakDict()
	{
		let tweaksInput = this.getInput('tweaks');
		let tweakResult = tweaksInput.getLastResult();
		if(!this.getHaveNonDefaultValues(tweakResult))
			return null;

		return {
			brightness: tweakResult.getValue('brightness')/100.0,
			contrast: tweakResult.getValue('contrast')/100.0,
			hue: tweakResult.getValue('hue')/100.0,
			saturation: tweakResult.getValue('saturation')/100.0,
			balance: [
				tweakResult.getValue('cyan_red')/100.0,
				tweakResult.getValue('magenta_green')/100.0,
				tweakResult.getValue('yellow_blue')/100.0,
			]
		};
	}

	generateSubclass(scene, inputValues, outputName, computedOutputResults)
	{	
		let pGeoList = inputValues['geo'];
		if(!pGeoList)
			return null;

		for(let pCachedInfo of this.cachedTextures)
			pCachedInfo.isUsed = false;

		let tweakParams = inputValues['tweaks'].getParameterInfos();

		// See if we're all at defaults to avoid creating dynamic textures unnecessarily:
		let bHaveNonDefaultValues = this.getHaveNonDefaultValues(inputValues['tweaks']);

		// Some meshes may have an instance of the same material id applied
		let uniqueMaterialsProcessed = {};
		for(let pSourceMesh of pGeoList)
		{
			pSourceMesh.parentMaterialTweaksNodeId = this.getId();

			let pSrcMaterial = pSourceMesh.material?.subMaterials ? pSourceMesh.material.subMaterials[0] : pSourceMesh.material;
			if(!pSrcMaterial || !pSrcMaterial.diffuseTexture)
				continue;

			if(uniqueMaterialsProcessed[pSrcMaterial.uniqueId])
				continue;
			
			uniqueMaterialsProcessed[pSrcMaterial.uniqueId] = true;

			if(!pSrcMaterial.diffuseTexture.isReadyOrNotBlocking() && !pSrcMaterial.diffuseTexture.isCached)
				continue;

			// We have disabled this because previously, the asset cacher would say the asset is "loaded" (and call
			// node callbacks) at the point where the Babylon file has finished loading, but not its underlying
			// textures. This lead to misleading results from the isLoading() function, which would say true, then
			// false, then true again once more assets start loading. This has now been fixed, so we shouldn't
			// need to manually re-dirty nodes here.
			/*
			{
				if(!this.recheckTimeout)
					this.recheckTimeout = setTimeout(() => {
						this.markDirty();
						this.recheckTimeout = null;
					}, 250);
				continue;
			}
			*/
			if(bHaveNonDefaultValues)
			{
				// Textures take time to load, so we cache textures based on the texture url
				let pCachedTexture = this.getCachedTexture(pSrcMaterial.diffuseTexture.url);
				if(!pCachedTexture)
				{
					// Add a new one
					pCachedTexture = new BABYLON.CustomProceduralTexture(`${pSrcMaterial.name}_procedural`, 'Tweak', pSrcMaterial.diffuseTexture.getSize(), scene);
					pCachedTexture.setFragment(pCachedTexture._texturePath);
					pCachedTexture.setTexture('u_image', pSrcMaterial.diffuseTexture);
					pCachedTexture.isCached = true;
					this.cachedTextures.push({url: pSrcMaterial.diffuseTexture.url, isUsed: true, texture: pCachedTexture});
				}

				// Otherwise, we have a texture, set the params:
				pCachedTexture.setTexture('u_image', pSrcMaterial.diffuseTexture);

				for(let parmInfo of tweakParams)
					pCachedTexture.setFloat(SHADER_INPUT_NAMES[parmInfo.name], parmInfo.value/100.0);

				pSrcMaterial.diffuseTexture = pCachedTexture;
			}
		}

		return inputValues['geo'];
	}
}